import React from 'react'
import { WifiOff } from 'lucide-react'
import { useCallStore } from '@/stores/callStore'
import { SafeAreaTop } from '@/components/ui/SafeAreaTop'

/**
 * AC-4: "Phone offline — reconnecting…" banner shown while the softphone is
 * retrying its access-token refresh with exponential backoff. Clears the
 * moment a refresh succeeds (callStore.phoneOffline flips to false).
 *
 * Neutral glass while reconnecting (waiting); blush once the retry breaker
 * trips (a failure). The Reconnect action is the primary button.
 * Rendered in App.tsx next to MicPermissionBanner — stacks above it.
 */
export function PhoneOfflineBanner(): React.ReactElement | null {
  const phoneOffline = useCallStore(s => s.phoneOffline)
  const exhausted = useCallStore(s => s.tokenRefreshExhausted)
  const forceReconnect = useCallStore(s => s.forceReconnect)
  if (!phoneOffline) return null

  return (
    <SafeAreaTop
      // Pin below the 3.5rem (56px) top header + a 12px gap.
      // SafeAreaTop owns env(safe-area-inset-top); never hand-roll it.
      extra="calc(3.5rem + 12px)"
      className="fixed top-0 left-0 right-0 z-[70] pointer-events-none animate-fade-up"
    >
    <div
      role="alert"
      aria-live="assertive"
      className="mx-auto max-w-sm w-[calc(100%-24px)] pointer-events-auto"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 14px',
          borderRadius: 'var(--r-md)',
          background: exhausted ? 'rgb(var(--blush-rgb) / 0.10)' : 'rgb(var(--fg-rgb) / 0.06)',
          border: `0.5px solid ${exhausted ? 'rgb(var(--blush-rgb) / 0.28)' : 'rgb(var(--fg-rgb) / 0.12)'}`,
          boxShadow: '0 10px 30px -8px rgba(0,0,0,0.6)',
          backdropFilter: 'blur(32px) saturate(180%)',
          WebkitBackdropFilter: 'blur(32px) saturate(180%)',
        }}
      >
        {/* Icon chip */}
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--r-sm)',
            background: exhausted ? 'rgb(var(--blush-rgb) / 0.16)' : 'rgb(var(--fg-rgb) / 0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <WifiOff style={{ width: 15, height: 15, color: exhausted ? 'var(--midnight-blush)' : 'rgb(var(--fg-rgb) / 0.70)' }} />
        </div>

        {/* Message */}
        <p style={{ fontSize: 13, color: 'rgb(var(--fg-rgb) / 0.80)', flex: 1, lineHeight: 1.4 }}>
          {exhausted ? (
            <>
              <span style={{ fontWeight: 600, color: 'var(--midnight-blush)' }}>Phone unavailable</span>
              {' — tap Reconnect or contact support'}
            </>
          ) : (
            <>
              <span style={{ fontWeight: 600, color: 'rgb(var(--fg-rgb))' }}>Phone offline</span>
              {' — reconnecting…'}
            </>
          )}
        </p>

        {/* Manual reconnect — only shown when the auto-retry breaker tripped
            (10 failed refreshes). Single source of "give up + try again" so
            the user isn't stuck refreshing the whole tab. */}
        {exhausted && (
          <button
            type="button"
            onClick={() => { void forceReconnect() }}
            aria-label="Retry phone connection"
            style={{
              padding: '6px 12px',
              borderRadius: 'var(--r-sm)',
              background: 'var(--accent)',
              border: '0.5px solid transparent',
              color: 'var(--accent-ink)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Reconnect
          </button>
        )}
      </div>
    </div>
    </SafeAreaTop>
  )
}
